var LEFT_BOUNDARY = [0, 1];
var RIGHT_BOUNDARY = [1, 0];
function fromString(raw) {
    var _a = raw.split('/').map(function (x) { return parseInt(x.trim()); }), n = _a[0], d = _a[1];
    return [n, d];
}
/**
 * Transforms an index to its string representation.
 * @param index Index to be transformed.
 * @returns Index string representation. `[1, 2] => 1/2`
 */
export function toString(index) {
    return index[0] + "/" + index[1];
}
/**
 * Determines whether an index is valid or not.
 * @param index Index to validate.
 * @returns `true` if the index is valid; otherwise `false`.
 */
export function isValid(index) {
    if (!index || index.length !== 2)
        return false;
    return index[0] > 0 && index[1] > 0;
}
/**
 * Compares two specified indexes and returns a number that indicates their relative position in the sort order.
 * @param left The left index to compare.
 * @param right The right index to compare.
 * @returns A number that indicates the relative position of the comparands:
 *  - result < 0: `left` precedes `right`.
 *  - result === 0: `left` is in the same position as `right`.
 *  - result > 1: `left` follows `right`.
 */
export function compare(left, right) {
    var isLeftValid = isValid(left);
    var isRightValid = isValid(right);
    if (!isLeftValid && !isRightValid)
        return 0;
    if (!isLeftValid)
        return -1;
    if (!isRightValid)
        return 1;
    return left[0] * right[1] - left[1] * right[0];
}
/**
 * Determines whether two indexes have the same value.
 * @remarks Indexes with different numbers could contain the same value. For example, `equals([1,1], [2,2])` is `true`.
 * @param left The left index to compare.
 * @param right The right index to compare.
 * @returns `true` if the value of `left` is the same as the value of `right`; otherwise, `false`.
 */
export function equals(left, right) {
    return compare(left, right) === 0;
}
/**
 * Determines whether the left index is greater than right index.
 * @param left The left index to compare.
 * @param right The right index to compare.
 * @returns `true` if `left` is greater than `right`; otherwise, `false`.
 */
export function gt(left, right) {
    return compare(left, right) > 0;
}
/**
 * Determines whether the left index is greater than or equal to right index.
 * @param left The left index to compare.
 * @param right The right index to compare.
 * @returns `true` if `left` is greater than or equal to `right`; otherwise, `false`.
 */
export function gte(left, right) {
    return compare(left, right) >= 0;
}
/**
 * Determines whether the left index is less than right index.
 * @param left The left index to compare.
 * @param right The right index to compare.
 * @returns `true` if `left` is less than `right`; otherwise, `false`.
 */
export function lt(left, right) {
    return compare(left, right) < 0;
}
/**
 * Determines whether the left index is less than or equal to right index.
 * @param left The left index to compare.
 * @param right The right index to compare.
 * @returns `true` if `left` is less than or equal to `right`; otherwise, `false`.
 */
export function lte(left, right) {
    return compare(left, right) <= 0;
}
function gcd(left, right) {
    while (right !== 0) {
        var prev = right;
        right = left % right;
        left = prev;
    }
    return left;
}
/**
 * Reduces an index to its simplest form. For example: `[6, 3]` => `[2, 1]`
 * @param index Index to be simplified.
 * @returns simplest form of provided index.
 */
export function simplify(index) {
    var cd = gcd(index[0], index[1]);
    return [index[0] / cd, index[1] / cd];
}
/**
 * Obtains the mediant index of two given indexes.
 * @remarks
 * The mediant of two indexes `m1/n1` and `m2/n2` is, by definition, the index `(m1 + m2)/(n1 + n2)`.
 *
 * If `m1/n1 < m2/n2` then `m1/n1 < (m1 + m2)/(n1 + n2) < m2/n2`.
 *
 * So, to get an index that can be placed between two indexes in a sort operation, the provided left index must be less than provided right index.
 * @param left The left index.
 * @param right The right index.
 * @returns The mediant index of left and right indexes
 */
export function mediant(left, right) {
    return simplify([left[0] + right[0], left[1] + right[1]]);
}
/**
 * Obtains a smaller index than the provided index.
 * @param index Index to use.
 * @returns a smaller index.
 */
export function smaller(index) {
    return mediant(LEFT_BOUNDARY, index);
}
/**
 * Obtains a bigger index than the provided index.
 * @param index Index to use.
 * @returns a bigger index.
 */
export function bigger(index) {
    return mediant(index, RIGHT_BOUNDARY);
}
/**
 * Generates a ordered list of indexes of the provided size.
 * @remarks
 * The list is a section of a flatten Stern-Brotol tree.
 * @param size Size of the produced list.
 * @returns an ordered list indexes of the given size.
 */
export function generate(size) {
    var result = [];
    if (size <= 0)
        return result;
    result.push(mediant(LEFT_BOUNDARY, RIGHT_BOUNDARY));
    if (size === 1)
        return result;
    var index = 0;
    while (result.length < size) {
        var current = result[index];
        if (index === 0)
            result.splice(index, 0, smaller(current));
        else
            result.splice(index, 0, mediant(current, result[index - 1]));
        if (result.length === size)
            return result;
        index = index + 2;
        if (index >= result.length)
            result.push(bigger(current));
        else
            result.splice(index, 0, mediant(current, result[index]));
        if (result.length === size)
            return result;
        index = index + 2;
        if (index >= result.length)
            index = 0;
    }
    return result;
}
/**
 * Tries to convert the string representation of a index to its `FractionalIndex` equivalent.
 * @remarks The `parse` function does not throw an error if it cannot parse the string value, it will return `undefined` instead.
 * @param raw String raw value to parse
 * @returns the parsed index value or `undefined` if the string cannot be parsed into an index.
 */
export function parse(raw) {
    try {
        var index = fromString(raw);
        return isValid(index) ? index : void 0;
    }
    catch (_a) {
        return void 0;
    }
}
